import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';

export interface ImagePopupDialogData {
  // Optional title for the pop-up
  title?: string;

  // Attempts to load image append "imageKey" to one of our external URLs.
  // If this is undefined, then imageUrl will be used.
  imageKey?: string;

  // Intended to be the fallback URL when "imageKey" doesn't work.
  fallbackImageUrl?: string;

  // If true then the <img> will be styled to fit the dialog width.
  fillWidth?: boolean;
}

@Component({
  selector: 'app-image-popup',
  templateUrl: './image-popup.component.html',
  styleUrls: ['./image-popup.component.scss'],
  standalone: false,
})
export class ImagePopupComponent implements OnInit {
  private _dialogRef = inject<MatDialogRef<ImagePopupComponent>>(MatDialogRef);

  // List of external URLs that we seem to use to host images.
  // Some are the same URL but uses a different image extension (e.g. jpeg / jpg)
  externalUrls = environment.suspectImageUrlConfigs;

  // ------------------ ImagePopupDialogData ------------------ //

  // Unique identifier of the image file passed by the parent component
  // This is meant to be appended to an external URL in our list.
  imageKey?: string;

  fallbackImageUrl?: string;

  // The built URL that is passed to our <img> tag
  // (may or may not succeed, hence why we try multiple URLs)
  url: string;

  fillWidth = false;

  title = '';

  // ------------------ ImagePopupDialogData ------------------ //

  // If image fails this counter will keep incrementing until we reach
  // the last URL in our list (this.urlConfigs)

  attempts: number;
  isLoadingImage = true;

  constructor() {
    const data = inject<ImagePopupDialogData>(MAT_DIALOG_DATA);

    this.title = data.title;
    this.imageKey = data.imageKey;
    this.fallbackImageUrl = data.fallbackImageUrl;
    this.fillWidth = data.fillWidth;
  }

  ngOnInit() {
    this.url = null;
    this.attempts = 0;
    // If this fails, it is called again by <img>'s error event
    this.tryLoadingImage();
  }

  buildUrl() {
    if (!this.imageKey && this.fallbackImageUrl) {
      this.url = this.fallbackImageUrl;
      this.isLoadingImage = false;
      return;
    }

    // Base case
    if (this.attempts >= this.externalUrls.length) {
      this.isLoadingImage = false;
      return;
    }

    // Get current external URL and construct a new image URL
    const { base_url, ext } = this.externalUrls[this.attempts];
    const fileName = this.imageKey + ext;

    // Update URL for <img>
    this.url = `${base_url}/${fileName}`;

    // In case of any errors, try another URL next round
    this.attempts++;
  }

  // This function is called during component mount & when the image fails to load.
  // The intention is to retry loading the image using another URL from our list (this.urlConfigs)
  tryLoadingImage() {
    this.buildUrl();
  }
}
